import { MarketData } from 'common/src/utils/types';
import { uploadMarketDataToDb } from 'common/src/api/market_data';
import { convertExcelDate } from 'common/src/utils/functions';
import { groupSizeAndColor } from '../utils/functions';
import { getSpeciesIdExcelNameMap } from 'common/src/api/species';

export const marketDataColumnMapping: { [key: string]: string } = {
  'TRANSPORT(AIR/OCEAN)': 'transport',
  'transport(air/ocean)': 'transport',
  'volume(pallet)': 'volume',
  'sold volume(pallet)': 'sold_volume',
  'price kg': 'price_kg',
  'grower no.': 'grower_no',
  'weight/kg': 'weight_kg',
  'price/kg': 'price_kg',
};

export class MarketDataProcessor {
  private static transformColumnName(columnName: string): string {
    const cleanName = columnName
      .replace(/[\r\n]+/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[（]/g, '(')
      .replace(/[）]/g, ')')
      .trim()
      .toLowerCase();

    return marketDataColumnMapping[cleanName] || cleanName;
  }

  private static transformValue(key: string, value: any): string | number {
    // First convert to string and trim if it's a string value
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    if (key === 'date') {
      return (
        convertExcelDate(trimmedValue)?.split('T')[0] ||
        new Date().toISOString()
      );
    }

    if (['volume', 'sold_volume', 'price', 'kg', 'price_kg'].includes(key)) {
      return Number(trimmedValue) || 0;
    }

    return (trimmedValue || '').toString();
  }

  static async transformData(rawData: any[]): Promise<Partial<MarketData>[]> {
    console.log('Raw data sample:', rawData[0]);

    // Obtener el mapa de excel_name a ID de especies una sola vez
    const speciesMap = await getSpeciesIdExcelNameMap();
    console.log('Species Map:', speciesMap);

    return rawData.map((row, index) => {
      const transformedRow: Partial<MarketData> = {};

      // Filter out any keys that contain '__empty'
      const validEntries = Object.entries(row).filter(
        ([key]) => !key.includes('__empty')
      );

      validEntries.forEach(([key, value]) => {
        const normalizedKey = this.transformColumnName(key);
        if (normalizedKey) {
          // Usar indexación tipada para evitar errores
          (transformedRow as any)[normalizedKey] = this.transformValue(
            normalizedKey,
            value
          );
        }
      });

      if (transformedRow.size) {
        const { size: processedSize, color } = groupSizeAndColor(
          transformedRow.size.toString()
        );
        transformedRow.size = processedSize;
        transformedRow.color = color;
      }

      // Obtener el excel_name (ITEM) y convertirlo a mayúsculas
      const excelName = (
        (transformedRow as any)?.item?.toString() || ''
      ).toUpperCase();

      // Buscar el ID numérico correspondiente en el mapa de especies
      if (excelName && speciesMap[excelName]) {
        // El species_id en MarketData debe ser bigint, así que convertirlo al tipo adecuado
        transformedRow.species_id = speciesMap[excelName] as any;
      } else {
        // Si no se encuentra, usar el valor predeterminado (ID para CHERRY, que debería ser 2)
      }

      // Validar campos requeridos
      const missingFields = ['size', 'color', 'price_kg'].filter((field) => {
        const value = (transformedRow as any)[field];
        return !value || value === '' || value === '""';
      });

      if (missingFields.length > 0) {
        throw new Error(
          `Fila ${index + 2}: Campos requeridos faltantes: ${missingFields.join(
            ', '
          )}`
        );
      }

      // Validar que price_kg sea un número válido
      if (isNaN(Number((transformedRow as any).price_kg))) {
        throw new Error(
          `Fila ${index + 2}: El campo 'price_kg' debe ser un número válido`
        );
      }

      return transformedRow;
    });
  }

  // Definimos una interfaz para las opciones de carga
  static async upload(
    data: Partial<MarketData>[],
    options?: { monthsParam?: number; speciesIds?: string[]; mode?: 'replace' | 'append' }
  ): Promise<any> {
    try {
      // Add debug logging
      console.log('Raw data before transformation:', data[0]); // Log first record
      console.log('Upload options:', options);

      const months = options?.monthsParam || 4;
      const speciesIds = options?.speciesIds;

      return await uploadMarketDataToDb(data, months, speciesIds, options?.mode ?? 'replace');
    } catch (error) {
      console.error('Error uploading market data:', error);
      return { success: false };
    }
  }
}
